import { useCallback, useEffect, useRef } from "react";
import { DialogPublic } from "../../../../components/DialogPublic/DialogPublic";
import { ISiteModuleImageGalleryItem } from "../../../../interfaces/ISiteModule";
import styles from "./GallerySlideshow.module.scss";
import { GallerySlideshowItem } from "./components/GallerySlideshowItem/GallerySlideshowItem";
import { IconLongArrowRight } from "../../../../icons/longArrowRight";

interface IProps {
    startItem: ISiteModuleImageGalleryItem;
    gallery: ISiteModuleImageGalleryItem[];
    handleClose: () => void;
}

export function GallerySlideshow(props: IProps) {
    const items = props.gallery;

    const startItemIndex = props.gallery.findIndex((item) => props.startItem === item);

    const containerRef = useRef<HTMLDivElement>(null);
    const itemRef = useRef<HTMLDivElement>(null); // ref to measure width

    const maxIndex = items.length - 1;

    const scrollToIndex = useCallback(
        (index: number) => {
            const itemWidth = itemRef.current?.offsetWidth;
            if (!containerRef.current || !itemWidth) return;
            const clampedIndex = Math.min(Math.max(index, 0), maxIndex);
            containerRef.current.scrollTo({
                left: clampedIndex * itemWidth,
                behavior: "smooth",
            });
        },
        [maxIndex]
    );

    useEffect(() => {
        scrollToIndex(startItemIndex);
    }, [scrollToIndex, startItemIndex]);

    const getCurrentIndex = () => {
        const itemWidth = itemRef.current?.offsetWidth;
        if (!containerRef.current || !itemWidth) return 0;
        const scrollLeft = containerRef.current.scrollLeft;
        const newIndex = Math.round(scrollLeft / itemWidth);
        return newIndex;
    };

    const prev = useCallback(() => {
        const currentIndex = getCurrentIndex();
        scrollToIndex(currentIndex - 1);
    }, [scrollToIndex]);

    const next = useCallback(() => {
        const currentIndex = getCurrentIndex();
        scrollToIndex(currentIndex + 1);
    }, [scrollToIndex]);

    // Keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent): void => {
            if (e.key === "ArrowLeft") {
                prev();
            } else if (e.key === "ArrowRight") {
                next();
            }
        };

        window.addEventListener("keydown", handleKeyDown);
        return () => window.removeEventListener("keydown", handleKeyDown);
    }, [next, prev]);

    return (
        <DialogPublic
            open={true}
            size="MAX"
            handleClose={props.handleClose}
            closableByOutsideClickAndEsc={true}
            noScroll={true}
            maxHeight={true}
            padding={false}
        >
            <div className={`${styles.wrapper}`}>
                <button onClick={prev} className={`${styles.button} ${styles.prev}`}>
                    <IconLongArrowRight />
                </button>
                <div className={styles.slides} ref={containerRef}>
                    {items.map((item, index) => (
                        <GallerySlideshowItem key={item.id} item={item} itemRef={index === 0 ? itemRef : undefined} />
                    ))}
                </div>
                <button onClick={next} className={`${styles.button} ${styles.next}`}>
                    <IconLongArrowRight />
                </button>
            </div>
        </DialogPublic>
    );
}
